import React, { useEffect, useMemo } from "react";
import classNames from "classnames";
import { ButtonTestIds } from "e2e-tests/testIds";

import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { QuestionTypes } from "@/constants/questionsIds";
import useCheckboxQuestion from "@/hooks/useCheckbox";
import { QuestionComponentProps } from "@/types/common";
import useAnswers from "@/utils/useAnswers";

const CheckboxInline = ({
    handleNext,
    question,
}: QuestionComponentProps<QuestionTypes.CheckboxInline>) => {
    const { answers } = useAnswers();
    const { checked, handleCheck, handleBulkCheck, getIsChecked, isContinueDisabled } =
        useCheckboxQuestion(question.maxChosenOptions);

    useEffect(() => {
        if (answers[question.id]) {
            handleBulkCheck(answers[question.id] as string[]);
        }
    }, [question.id, answers, handleBulkCheck]);

    const sendAnalyticsEvents = () => {
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: checked,
        });
        setAnalyticsUserProperties({ [question.id]: checked });
        sendMetrics(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: checked,
        });
    };

    const handleContinue = () => {
        handleNext({ [question.id]: checked });
        sendAnalyticsEvents();
    };

    const answersItems = useMemo(
        () =>
            question.answers?.map(({ id, img, selectedImg, text }) => (
                <button
                    key={id}
                    data-zing-test-id={ButtonTestIds.CHECKBOX_OPTION}
                    className="checkbox relative rounded-2xl text-white [text-align:inherit]"
                    onClick={() => handleCheck(id)}
                >
                    <div className="flex h-full w-full flex-col items-center justify-between gap-1">
                        {(selectedImg || img) && (
                            <img
                                src={getIsChecked(id) ? selectedImg || img : img}
                                alt={text}
                                className="h-20 w-20"
                            />
                        )}
                        <div
                            className={classNames(
                                "flex w-full flex-1 justify-center text-center text-zing-16-22",
                                {
                                    "text-zing-neutral-cool-grey-600": !getIsChecked(id),
                                    "text-white female:text-black": getIsChecked(id),
                                }
                            )}
                        >
                            <span className="max-w-[12.5rem] font-semibold">{text}</span>
                        </div>
                    </div>
                </button>
            )),
        [question.answers, handleCheck, getIsChecked]
    );

    return (
        <div className="grid h-full grow flex-col content-between gap-8">
            <div className="relative grid gap-6">
                <div className="grid gap-4">
                    <div className="z-50 whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black">
                        {question.title}
                    </div>
                </div>
                <div className="relative grid grid-cols-3 gap-y-6">{answersItems}</div>
            </div>
            <StickyButton
                onClick={handleContinue}
                isDisabled={isContinueDisabled}
                className={classNames("transition-all", {
                    "pointer-events-none invisible opacity-0": isContinueDisabled,
                })}
            />
        </div>
    );
};

export default CheckboxInline;
